import React, { useState, useEffect } from "react";
import { notification, Input, Button, message, Modal, Typography } from "antd";
import styles from "./index.module.css";
import logo from "../../utils/img/loginLogo.png";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  verifyEmail,
  verifyOTP,
  retreiveValues,
  deleteStep,
  getForm,
} from "../../store/policy";
import {
  checkEmailExists,
  resetEmailCacheForStep,
} from "../../store/medicalAssessmentSlice";
import {
  ShieldCheck,
  Mail,
  Clock,
  ArrowRight,
  KeyRound,
  X,
  RefreshCw,
  FileText,
} from "lucide-react";

const { Title, Text, Paragraph } = Typography;

function Index() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [Otp, setOTP] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [err, setErr] = useState(false);
  const [otpReq, setOtpReq] = useState("");
  const [hasExistingData, setHasExistingData] = useState(false);
  const [messageapi, context] = message.useMessage();
  const [api, Apicontext] = notification.useNotification();

  const { buttonLoading, otpLoading, userId, form } = useSelector(
    (state) => state.policySlice,
  );

  const { emailCheckStatus } = useSelector((state) => state.medicalAssessment);

  useEffect(() => {
    localStorage.clear();
    sessionStorage.clear();
    dispatch(resetEmailCacheForStep("main-page"));
  }, []);

  const handlePostLoginRedirect = async (userId, stepId) => {
    const formRes = await dispatch(getForm());
    const formData = formRes?.payload;

    if (stepId && formData) {
      const retrieveRes = await dispatch(retreiveValues(userId));
      if (retrieveRes?.payload?.result) {
        const savedData = [...retrieveRes.payload.result];
        const coverageCountry = savedData
          ?.find((step) => step?.source === "user_information")
          ?.parameters?.find(
            (field) => field?.key === "policy_coverage_country",
          )?.value;
        if (coverageCountry) {
          localStorage.setItem("coverageCountry", coverageCountry);
        }
      }

      for (let i = 0; i < formData.length; i++) {
        const steps = formData[i]?.service_options?.[0]?.steps;
        const stepIndex = steps?.findIndex((step) => step === stepId);
        if (stepIndex >= 0) {
          const isEstimateForm = formData[i]?.sequence === 1;
          const nextStep = stepIndex + 2;

          if (isEstimateForm) {
            const cappedStep = nextStep > 3 ? 3 : nextStep;
            sessionStorage.setItem("lastStep", cappedStep);
            navigate("/estimate");
          } else {
            sessionStorage.setItem("lastStep", nextStep + 3);
            navigate("/quotation");
          }
          return;
        }
      }

      navigate("/estimate");
    } else {
      navigate("/estimate");
    }
  };

  const submitOtp = () => {
    if (!Otp) {
      setOtpReq("Please enter the OTP!");
      return;
    }
    if (Otp.toString().length < 6) {
      setOtpReq("Please enter the correct OTP!");
      return;
    }
    dispatch(verifyOTP({ email: email, otp: Otp })).then((res) => {
      if (res?.payload?.result?.verified) {
        localStorage.setItem("userId", res?.payload?.result?.user_id);
        localStorage.setItem("token", res?.payload?.result?.token);
        setIsModalOpen(false);
        message.success("Your E-mail is verified");
        dispatch(getForm());
        if (res?.payload?.result?.step_id) {
          localStorage.setItem("step", res?.payload?.result?.step_id);
          setHasExistingData(true);
        } else {
          navigate("/estimate");
        }
      }
    });
  };

  const verifyUserEmail = async () => {
    if (!email) {
      setErr("Please enter your email!");
      return;
    }

    if (!/^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]+$/.test(email)) {
      setErr("Please follow the email pattern!");
      return;
    }

    const result = await dispatch(
      checkEmailExists({
        email: email,
        stepId: "main-page",
      }),
    );

    if (result.payload?.exists) {
      return;
    }

    dispatch(verifyEmail({ email, api })).then((res) => {
      if (res?.payload?.result.email_sent) {
        setIsModalOpen(true);
      } else if (res?.payload?.result?.verified) {
        if (res?.payload?.result?.user_id) {
          localStorage.setItem("userId", res?.payload?.result?.user_id);
        }
        if (res?.payload?.result?.token) {
          localStorage.setItem("token", res?.payload?.result?.token);
        }
        message.success("Your E-mail is verified");
        dispatch(getForm());
        if (res?.payload?.result?.step_id) {
          localStorage.setItem("step", res?.payload?.result?.step_id);
          setHasExistingData(true);
        } else {
          navigate("/estimate");
        }
      }
    });
  };

  const features = [
    {
      icon: <ShieldCheck size={20} />,
      text: "Secure verification to protect your data",
    },
    {
      icon: <Mail size={20} />,
      text: "Personalized quote delivered to your inbox",
    },
    {
      icon: <Clock size={20} />,
      text: "Resume your application anytime",
    },
  ];

  return (
    <>
      {context}
      {Apicontext}

      <div className={styles.page}>
        {/* Background decorations */}
        <div className={styles.bgGrid} />
        <div className={styles.bgGlow1} />
        <div className={styles.bgGlow2} />
        <div className={styles.bgGlow3} />

        {/* Floating bubbles */}
        <div className={styles.bubbles}>
          {[...Array(12)].map((_, i) => (
            <span key={i} className={styles.bubble} />
          ))}
        </div>

        <div className={styles.container}>
          {/* Left - Branding Panel */}
          <div className={styles.brandPanel}>
            <div className={styles.brandContent}>
              <div className={styles.logoBox}>
                <img src={logo} alt="Eden Care" className={styles.logo} />
              </div>
              <Title level={2} className={styles.brandTitle}>
                Insurance made simple.
              </Title>
              <Paragraph className={styles.brandDesc}>
                Get a personalized health insurance quote in minutes. Our
                streamlined process makes finding the right coverage effortless.
              </Paragraph>

              <div className={styles.featureList}>
                {features.map((f, i) => (
                  <div
                    key={i}
                    className={styles.featureItem}
                    style={{ animationDelay: `${0.6 + i * 0.15}s` }}
                  >
                    <span className={styles.featureIcon}>{f.icon}</span>
                    <Text className={styles.featureText}>{f.text}</Text>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.brandFooter}>
              <div className={styles.trustBadges}>
                <ShieldCheck size={14} />
                <Text className={styles.trustText}>256-bit SSL Encrypted</Text>
                <span className={styles.trustDot} />
                <Text className={styles.trustText}>GDPR Compliant</Text>
              </div>
            </div>
          </div>

          {/* Right - Form Panel */}
          <div className={styles.formPanel}>
            <div className={styles.formContent}>
              <div className={styles.formHeader}>
                <Title level={3} className={styles.formTitle}>
                  Get Your Quote
                </Title>
                <Paragraph className={styles.formSubtitle}>
                  Enter your email to get started
                </Paragraph>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>Email Address</label>
                <Input
                  size="large"
                  className={styles.emailInput}
                  prefix={
                    <Mail
                      size={18}
                      color={email ? "#028D86" : "#94a3b8"}
                      style={{ transition: "color 0.3s" }}
                    />
                  }
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setErr("");
                    setEmail(e.target.value);
                  }}
                  onPressEnter={verifyUserEmail}
                />
                <div className={styles.errorMsg}>{err || "\u00A0"}</div>
              </div>

              <Button
                type="primary"
                className={styles.submitBtn}
                loading={buttonLoading || emailCheckStatus === "loading"}
                onClick={verifyUserEmail}
                size="large"
                block
              >
                <span>Continue</span>
                {!buttonLoading && emailCheckStatus !== "loading" && (
                  <ArrowRight size={18} style={{ marginLeft: 8 }} />
                )}
              </Button>

              <div className={styles.formDivider}>
                <span />
                <Text className={styles.dividerText}>Safe & Secure</Text>
                <span />
              </div>

              <Paragraph className={styles.disclaimer}>
                By continuing, you agree to receive a one-time verification code
                at the email provided. We'll never spam you.
              </Paragraph>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      <Modal
        footer={null}
        open={isModalOpen}
        width={420}
        onCancel={() => setIsModalOpen(false)}
        centered
        closable={false}
        styles={{
          content: {
            borderRadius: 20,
            padding: 0,
            overflow: "hidden",
          },
          body: { padding: 0 },
        }}
      >
        <div className={styles.otpModal}>
          <button
            className={styles.modalClose}
            onClick={() => setIsModalOpen(false)}
          >
            <X size={18} />
          </button>

          <div className={styles.otpIconWrap}>
            <KeyRound size={28} color="#028D86" />
          </div>

          <Title level={4} className={styles.otpTitle}>
            Check your email
          </Title>
          <Paragraph className={styles.otpDesc}>
            We sent an 8-digit code to <strong>{email}</strong>
          </Paragraph>

          <Input
            className={styles.otpInput}
            size="large"
            value={Otp}
            placeholder="Enter code"
            maxLength={8}
            onChange={(e) => {
              if (
                e.target.value
                  .split("")
                  .every((val) => !isNaN(parseInt(val))) ||
                !e.target.value
              ) {
                setOTP(e.target.value);
                setOtpReq("");
              }
            }}
            onPressEnter={submitOtp}
          />
          {otpReq && <div className={styles.otpError}>{otpReq}</div>}

          <Button
            type="primary"
            size="large"
            block
            loading={otpLoading}
            className={styles.otpSubmitBtn}
            onClick={submitOtp}
          >
            Verify
          </Button>

          <button
            className={styles.resendLink}
            onClick={() => {
              dispatch(verifyEmail({ email, api }));
            }}
          >
            Didn't receive it? Resend code
          </button>
        </div>
      </Modal>

      {/* Existing Application Modal */}
      <Modal
        footer={null}
        open={hasExistingData}
        width={440}
        closable={false}
        centered
        styles={{
          content: {
            borderRadius: 20,
            padding: 0,
            overflow: "hidden",
          },
          body: { padding: 0 },
        }}
      >
        <div className={styles.existModal}>
          <div className={styles.existIconWrap}>
            <FileText size={28} color="#028D86" />
          </div>

          <Title level={4} className={styles.existTitle}>
            Welcome back!
          </Title>
          <Paragraph className={styles.existDesc}>
            You have an application in progress. Would you like to pick up where
            you left off?
          </Paragraph>

          <div className={styles.existActions}>
            <Button
              size="large"
              className={styles.existSecondaryBtn}
              onClick={() => {
                dispatch(deleteStep(localStorage.getItem("userId")));
                sessionStorage.removeItem("values");
                navigate("/estimate");
              }}
              icon={<RefreshCw size={16} />}
            >
              Start Fresh
            </Button>
            <Button
              type="primary"
              size="large"
              className={styles.existPrimaryBtn}
              onClick={() => {
                handlePostLoginRedirect(
                  localStorage.getItem("userId"),
                  localStorage.getItem("step"),
                );
              }}
              icon={<ArrowRight size={16} />}
            >
              Continue
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default Index;
